import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { 
  Plus, Edit2, Trash2, Calendar, Percent, Building2, 
  Save, X, Filter, ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const DEPARTMENTS = ["PRQC", "LMDS", "Location", "GLOBAL"];

export const GestionVentilation = () => {
  const [loading, setLoading] = useState(true);
  const [regles, setRegles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRegle, setEditingRegle] = useState(null);
  const [formData, setFormData] = useState({
    categorie: "",
    compte_comptable: "",
    montant_exact: "",
    departement_cible: "PRQC",
    pourcentage: "100",
    date_debut: "2024-01-01",
    date_fin: "",
    actif: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reglesRes, catsRes] = await Promise.all([
        axios.get(`${window.__API_URL__ || ""}/api/ventilation/rules`),
        axios.get(`${window.__API_URL__ || ""}/api/classification/categories`)
      ]);
      
      const reglesData = reglesRes.data;
      const catsData = catsRes.data;
      
      setRegles(reglesData.rules || []);
      setCategories(catsData.categories || []);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (regle = null) => {
    if (regle) {
      setEditingRegle(regle);
      setFormData({
        categorie: regle.categorie || "",
        compte_comptable: regle.compte_comptable || "",
        montant_exact: regle.montant_exact || "",
        departement_cible: regle.departement_cible || "PRQC",
        pourcentage: (regle.pourcentage * 100).toString(),
        date_debut: regle.date_debut || "2024-01-01",
        date_fin: regle.date_fin || "",
        actif: regle.actif !== false
      });
    } else {
      setEditingRegle(null);
      setFormData({
        categorie: "",
        compte_comptable: "",
        montant_exact: "",
        departement_cible: "PRQC",
        pourcentage: "100",
        date_debut: "2024-01-01",
        date_fin: "",
        actif: true
      });
    }
    setIsDialogOpen(true);
  };

  const saveRegle = async () => {
    if (!formData.categorie) {
      toast.error("Veuillez sélectionner une catégorie");
      return;
    }

    const pct = parseFloat(formData.pourcentage);
    if (isNaN(pct) || pct <= 0 || pct > 100) {
      toast.error("Pourcentage doit être entre 1 et 100");
      return;
    }

    try {
      const payload = {
        ...formData,
        pourcentage: pct / 100,
        montant_exact: formData.montant_exact ? parseFloat(formData.montant_exact) : null
      };

      const response = await axios.post(`${window.__API_URL__ || ""}/api/ventilation/rules`, payload);

      if (response.status === 200) {
        toast.success("✅ Règle de ventilation enregistrée!");
        setIsDialogOpen(false);
        loadData();
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const deleteRegle = async (regleId) => {
    if (!confirm("Supprimer cette règle de ventilation?")) return;

    try {
      const response = await axios.delete(`${window.__API_URL__ || ""}/api/ventilation/rules/${regleId}`);

      if (response.status === 200) {
        toast.success("Règle supprimée");
        loadData();
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  // Grouper par catégorie
  const reglesByCategory = {};
  regles.forEach(r => {
    const cat = r.categorie || "Sans catégorie";
    if (!reglesByCategory[cat]) {
      reglesByCategory[cat] = [];
    }
    reglesByCategory[cat].push(r);
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between animate-fade-in">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Gestion des Règles de Ventilation</h1>
          <p className="text-slate-500 mt-1">
            Définissez comment répartir les dépenses entre départements selon les dates
          </p>
        </div>
        <Button onClick={() => openDialog()} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle règle
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-fade-in delay-100">
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Total règles</p>
            <p className="text-2xl font-bold text-slate-900">{regles.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Règles actives</p>
            <p className="text-2xl font-bold text-emerald-600">
              {regles.filter(r => r.actif).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Catégories couvertes</p>
            <p className="text-2xl font-bold text-blue-600">
              {Object.keys(reglesByCategory).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Liste des règles par catégorie */}
      <div className="space-y-4">
        {Object.entries(reglesByCategory).map(([cat, catRegles]) => (
          <Card key={cat}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{cat}</CardTitle>
                <Badge>{catRegles.length} règle(s)</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {catRegles.map((regle, idx) => (
                  <div 
                    key={regle.id || idx}
                    className="border rounded-lg p-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">
                            <Building2 className="w-3 h-3 mr-1" />
                            {regle.departement_cible}
                          </Badge>
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                            <Percent className="w-3 h-3 mr-1" />
                            {(regle.pourcentage * 100).toFixed(0)}%
                          </Badge>
                          {!regle.actif && (
                            <Badge variant="outline" className="bg-red-50 text-red-700">
                              Inactive
                            </Badge>
                          )}
                        </div>
                        
                        <div className="text-sm space-y-1">
                          {regle.compte_comptable && (
                            <p className="text-slate-600">
                              📋 Compte: <span className="font-mono">{regle.compte_comptable}</span>
                            </p>
                          )}
                          {regle.montant_exact && (
                            <p className="text-slate-600">
                              💰 Montant exact: {regle.montant_exact.toFixed(2)} $
                            </p>
                          )}
                          <div className="flex items-center gap-2 text-slate-500">
                            <Calendar className="w-3 h-3" />
                            <span>
                              {regle.date_debut || "Début"} 
                              <ArrowRight className="w-3 h-3 inline mx-1" />
                              {regle.date_fin || "∞"}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openDialog(regle)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteRegle(regle.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dialog Création/Édition */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingRegle ? "Modifier" : "Nouvelle"} règle de ventilation
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Catégorie */}
            <div>
              <Label>Catégorie</Label>
              <Select 
                value={formData.categorie}
                onValueChange={(v) => setFormData({...formData, categorie: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une catégorie..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {categories.map(cat => (
                    <SelectItem key={cat.categorie} value={cat.categorie}>
                      {cat.categorie}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Critères (optionnels) */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Compte comptable (optionnel)</Label>
                <Input
                  placeholder="Ex: 2131 Master CIBC"
                  value={formData.compte_comptable}
                  onChange={(e) => setFormData({...formData, compte_comptable: e.target.value})}
                />
              </div>
              <div>
                <Label>Montant exact (optionnel)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 1168.00"
                  value={formData.montant_exact}
                  onChange={(e) => setFormData({...formData, montant_exact: e.target.value})}
                />
              </div>
            </div>

            {/* Ventilation */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Département cible</Label>
                <Select 
                  value={formData.departement_cible}
                  onValueChange={(v) => setFormData({...formData, departement_cible: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Pourcentage (%)</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.pourcentage}
                  onChange={(e) => setFormData({...formData, pourcentage: e.target.value})}
                />
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={formData.date_debut}
                  onChange={(e) => setFormData({...formData, date_debut: e.target.value})}
                />
              </div>
              <div>
                <Label>Date de fin (optionnelle)</Label>
                <Input
                  type="date"
                  value={formData.date_fin}
                  onChange={(e) => setFormData({...formData, date_fin: e.target.value})}
                  placeholder="Laisser vide pour ∞"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Laisser vide si la règle est permanente
                </p>
              </div>
            </div>

            {/* Exemple */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-900">
                <strong>Exemple:</strong> Pour "Essence" sur carte "2131 Master CIBC" du 01-01-2024 au 20-01-2026:
                30% PRQC, puis après 21-01-2026: 100% PRQC
              </p>
              <p className="text-xs text-blue-700 mt-1">
                → Créez 2 règles: une avec date_fin 20-01-2026 (30%), une autre avec date_debut 21-01-2026 (100%)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
            <Button onClick={saveRegle} className="bg-emerald-600 hover:bg-emerald-700">
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionVentilation;
